import { format, parse, subMonths } from 'date-fns';
import type { OrganizationOverviewData } from '@/shared/types/overviewTypes';

/** Saturated hues for market series — cyan, violet, green, orange, pink (cycles with modulo). */
export const ACCOUNT_CHART_PALETTE = ['#06b6d4', '#8b5cf6', '#22c55e', '#fb923c', '#ec4899'] as const;

/** Full opacity so bar/legend swatches match summary `text-*-500` hues. */
export const CHART_OPACITY = {
  marketBar: 1,
  marketArea: 0.48,
  fleetStack: 1,
  datapoolStack: 1,
  terminalTypeStack: 1,
} as const;

/** Last 12 months as `MMM-yyyy`, newest first. */
export const REVERSE_MONTH_KEYS = (() => {
  const now = new Date();
  const months: string[] = [];
  for (let i = 0; i < 12; i++) {
    months.push(format(subMonths(now, i), 'MMM-yyyy'));
  }
  return months;
})();

export type MonthRangeOption = 1 | 3 | 6 | 12;

export function monthKeyFromStartDate(isoDate: string): string {
  return format(new Date(isoDate), 'MMM-yyyy');
}

/** Chronological ascending compare for `MMM-yyyy` keys (localeCompare is not calendar-order). */
export function compareMonthKeysAsc(a: string, b: string): number {
  return parse(a, 'MMM-yyyy', new Date()).getTime() - parse(b, 'MMM-yyyy', new Date()).getTime();
}

export function calendarPrevYm(ym: string): string {
  const d = subMonths(parse(ym, 'MMM-yyyy', new Date()), 1);
  return format(d, 'MMM-yyyy');
}

export function gbToTb(gb: number): number {
  return gb / 1000;
}

export function getChartMonthTimeline(selectedOrganizations: OrganizationOverviewData[]): string[] {
  const keys = new Set<string>();
  for (const organization of selectedOrganizations) {
    organization.summary.forEach((p) => {
      keys.add(monthKeyFromStartDate(p.startDate));
    });
  }
  if (keys.size === 0) return [...REVERSE_MONTH_KEYS];
  return [...keys].sort(compareMonthKeysAsc);
}

/** Rounded top corners on stacked columns (matches global-dashboard bar visuals). */
export const BAR_TOP_RADIUS: [number, number, number, number] = [6, 6, 0, 0];

export const BAR_CHART_MAX_BAR_SIZE = 52;
export const BAR_CHART_CATEGORY_GAP = '26%';

export const BAR_MARGIN_PER_MARKET = { top: 40, right: 0, left: 0, bottom: 0 };
export const BAR_MARGIN_TIME_SERIES = { top: 40, right: 0, left: 0, bottom: 0 };

export const TICK_FONT_SIZE = 13;
export const LABEL_FONT_SIZE = 14;

export enum SectionType {
  FLEET_OVERVIEW,
  DATA_CONSUMPTION,
  TERMINAL_INVENTORY,
}

export function buildAccountChartColorMap(organizations: OrganizationOverviewData[]): Map<string, string> {
  const sorted = [...organizations].sort((a, b) => a.organizationId.localeCompare(b.organizationId));
  const map = new Map<string, string>();
  sorted.forEach((a, i) => {
    map.set(a.organizationId, ACCOUNT_CHART_PALETTE[i % ACCOUNT_CHART_PALETTE.length]!);
  });
  return map;
}